import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import { useTheme } from '../../lib/theme';
import { SettingsCard, primaryBtn, textInput } from '../settings/_shared';
import {
  getQbEntities, getQbMappings, updateQbMappings,
  getCategories, getTaxCodes,
} from '../../lib/api';

const PAYMENT_METHODS = [
  'Credit Card',
  'Debit Card',
  'Cash',
  'Bank Transfer',
  'Cheque',
];

function groupCoAByType(coa = []) {
  const groups = new Map();
  coa.forEach((a) => {
    const key = a.account_type || 'Other';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(a);
  });
  return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
}

function Select({ value, onChange, children, testid, colors }) {
  return (
    <select
      data-testid={testid}
      value={value || ''}
      onChange={(e) => onChange(e.target.value || '')}
      style={{ ...textInput(colors), maxWidth: 360 }}
    >
      {children}
    </select>
  );
}

export default function QuickBooksMappings({ connection, connections, onSelectConnection, onToast }) {
  const { colors } = useTheme();
  const [entities, setEntities] = useState(null);
  const [mappings, setMappings] = useState({
    category_mappings: {}, tax_mappings: {}, payment_mappings: {},
  });
  const [clasifiCategories, setClasifiCategories] = useState([]);
  const [clasifiTaxCodes, setClasifiTaxCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!connection?.id) return;
    setLoading(true);
    try {
      const [ents, maps, cats, taxCat] = await Promise.all([
        getQbEntities(connection.id),
        getQbMappings(connection.id),
        getCategories(),
        getTaxCodes().catch(() => ({ federal: [], provincial: [] })),
      ]);
      setEntities(ents);
      setMappings({
        category_mappings: maps.category_mappings || {},
        tax_mappings: maps.tax_mappings || {},
        payment_mappings: maps.payment_mappings || {},
      });
      setClasifiCategories((cats.categories || []).filter((c) => !c.is_special));
      const taxCodes = [
        ...(taxCat.federal || []),
        ...(taxCat.provincial || []),
      ];
      setClasifiTaxCodes(taxCodes);
    } catch (e) {
      onToast?.(`Could not load mappings: ${e.detail || 'network error'}`);
    } finally {
      setLoading(false);
    }
  }, [connection?.id, onToast]);

  useEffect(() => { load(); }, [load]);

  const coaGroups = useMemo(() => groupCoAByType(entities?.coa || []), [entities?.coa]);

  const setCategoryMap = (catId, qbId) => {
    setMappings((prev) => ({ ...prev, category_mappings: { ...prev.category_mappings, [catId]: qbId } }));
  };
  const setTaxMap = (code, qbId) => {
    setMappings((prev) => ({ ...prev, tax_mappings: { ...prev.tax_mappings, [code]: qbId } }));
  };
  const setPaymentMap = (method, qbId) => {
    setMappings((prev) => ({ ...prev, payment_mappings: { ...prev.payment_mappings, [method]: qbId } }));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      // Strip empty strings before sending so the backend ignores them.
      const cleanse = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v));
      await updateQbMappings(connection.id, {
        category_mappings: cleanse(mappings.category_mappings),
        tax_mappings: cleanse(mappings.tax_mappings),
        payment_mappings: cleanse(mappings.payment_mappings),
      });
      onToast?.('Mappings saved');
    } catch (e) {
      onToast?.(`Save failed: ${e.detail || 'network error'}`);
    } finally {
      setSaving(false);
    }
  };

  const bucketPill = (bucket) => (
    <span
      style={{
        display: 'inline-block', padding: '1px 8px', borderRadius: 999,
        background: colors.accentSoft, color: colors.accent,
        fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
        textTransform: 'uppercase', marginLeft: 8,
      }}
    >
      {bucket || 'expense'}
    </span>
  );

  if (loading || !entities) {
    return (
      <SettingsCard testid="qb-mappings-loading">
        <div style={{ color: colors.textMuted, fontSize: 13 }}>Loading mappings…</div>
      </SettingsCard>
    );
  }

  const hasSnapshot = (entities.coa?.length || 0) > 0;

  return (
    <SettingsCard
      testid="qb-mappings-card"
      title={<span>Mappings · <span style={{ color: colors.textMuted, fontWeight: 500 }}>{connection.company_name}</span></span>}
      description="Map your Clasifi categories, tax codes, and payment methods to their QuickBooks equivalents. These mappings are used when you sync out in a future phase."
      actions={connections.length > 1 ? (
        <select
          data-testid="qb-mappings-conn-select"
          value={connection.id}
          onChange={(e) => onSelectConnection(e.target.value)}
          style={{ ...textInput(colors), maxWidth: 240 }}
        >
          {connections.map((c) => (
            <option key={c.id} value={c.id}>{c.company_name}</option>
          ))}
        </select>
      ) : null}
    >
      {!hasSnapshot && (
        <div
          data-testid="qb-mappings-no-snapshot"
          style={{
            padding: 16, borderRadius: 12,
            background: colors.bg, border: `1px dashed ${colors.border}`,
            color: colors.textMuted, fontSize: 13,
          }}
        >
          No entities imported yet. Click “Refresh” on the connection above to pull your chart of accounts, vendors, and tax codes from QuickBooks.
        </div>
      )}

      {hasSnapshot && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {/* ---------- CATEGORIES → CoA ---------- */}
          <section data-testid="qb-mappings-categories">
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: colors.textMuted }}>
              Categories → QB Chart of Accounts
            </h3>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {clasifiCategories.map((c) => (
                <div
                  key={c.id}
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center',
                    padding: '10px 12px', borderRadius: 10,
                    background: colors.surface, border: `1px solid ${colors.border}`,
                  }}
                >
                  <div style={{ color: colors.text, fontSize: 14 }}>
                    <span style={{ fontWeight: 600 }}>{c.name}</span>
                    {bucketPill(c.bucket)}
                  </div>
                  <Select
                    testid={`qb-map-cat-${c.id}`}
                    colors={colors}
                    value={mappings.category_mappings[c.id] || ''}
                    onChange={(v) => setCategoryMap(c.id, v)}
                  >
                    <option value="">— unmapped —</option>
                    {coaGroups.map(([type, items]) => (
                      <optgroup key={type} label={type}>
                        {items.map((a) => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </optgroup>
                    ))}
                  </Select>
                </div>
              ))}
              {clasifiCategories.length === 0 && (
                <div style={{ color: colors.textMuted, fontSize: 13 }}>No categories to map.</div>
              )}
            </div>
          </section>

          {/* ---------- TAX CODES → QB TaxCodes ---------- */}
          <section data-testid="qb-mappings-taxcodes">
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: colors.textMuted }}>
              Tax Treatments → QB Tax Codes
            </h3>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {clasifiTaxCodes.length === 0 && (
                <div style={{ color: colors.textMuted, fontSize: 13 }}>No Clasifi tax codes defined for your province yet.</div>
              )}
              {clasifiTaxCodes.map((t) => (
                <div
                  key={t.code}
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center',
                    padding: '10px 12px', borderRadius: 10,
                    background: colors.surface, border: `1px solid ${colors.border}`,
                  }}
                >
                  <div style={{ color: colors.text, fontSize: 14 }}>
                    <span style={{ fontWeight: 600 }}>{t.label}</span>
                    <span style={{ marginLeft: 10, color: colors.textMuted, fontSize: 12 }}>{(t.rate * 100).toFixed(2)}%</span>
                  </div>
                  <Select
                    testid={`qb-map-tax-${t.code}`}
                    colors={colors}
                    value={mappings.tax_mappings[t.code] || ''}
                    onChange={(v) => setTaxMap(t.code, v)}
                  >
                    <option value="">— unmapped —</option>
                    {(entities.tax_codes || []).map((qt) => (
                      <option key={qt.id} value={qt.id}>
                        {qt.name}{qt.rate != null ? ` (${(qt.rate).toFixed(2)}%)` : ''}
                      </option>
                    ))}
                  </Select>
                </div>
              ))}
            </div>
          </section>

          {/* ---------- PAYMENT METHODS → QB Accounts ---------- */}
          <section data-testid="qb-mappings-payments">
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: colors.textMuted }}>
              Payment Methods → QB Payment Accounts
            </h3>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {PAYMENT_METHODS.map((m) => (
                <div
                  key={m}
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center',
                    padding: '10px 12px', borderRadius: 10,
                    background: colors.surface, border: `1px solid ${colors.border}`,
                  }}
                >
                  <div style={{ color: colors.text, fontSize: 14, fontWeight: 600 }}>{m}</div>
                  <Select
                    testid={`qb-map-pay-${m.replace(/\s/g, '-')}`}
                    colors={colors}
                    value={mappings.payment_mappings[m] || ''}
                    onChange={(v) => setPaymentMap(m, v)}
                  >
                    <option value="">— unmapped —</option>
                    {(entities.payment_accounts || []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} <span>({p.account_type})</span>
                      </option>
                    ))}
                  </Select>
                </div>
              ))}
            </div>
          </section>

          <div>
            <button
              type="button"
              data-testid="qb-mappings-save"
              onClick={onSave}
              disabled={saving}
              style={{ ...primaryBtn(colors, saving), display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <Save size={14} />
              {saving ? 'Saving…' : 'Save mappings'}
            </button>
          </div>
        </div>
      )}
    </SettingsCard>
  );
}
